import {useEffect, useMemo, useRef} from 'react';
import {isBackendUnsupportedQuery} from '@/lib/graphqlErrors';
import {useLocation, useNavigate} from 'react-router';
import {useQuery} from '@apollo/client';
import {useAtomValue, useSetAtom} from 'jotai';
import dayjs from 'dayjs';
import {
   TankGaugeReading_AllWhereDocument,
   Tank_AllWhereDocument,
   Well_AllDocument,
   Report_TankAllocationsDocument,
   PulledBottom_AllWhereDocument,
} from '@/graphql/generated/graphql.ts';
import {toWell} from '@/lib/wellMapping';
import {
   dedupeAllocationRowsByWellAndTank,
   expandLeaseRowsToTanks,
   leaseAllocationReportArgs,
} from '@/lib/allocationReportArgs';
import {toTankGaugeReadings, computeAllocatedOilProduction} from '@/lib/tankGaugeMapping';
import {toPulledBottoms} from '@/lib/pulledBottomMapping';
import {
   computeTankDropEvents,
   explainTankDrop,
   localCalendarDate,
   type TankDropTank,
   type RunTicketTankCandidate,
} from '@/lib/tankDropReconciliation';
import type {Well} from '@/types/well';
import {routerLinks} from '@/Constants.ts';
import {MAX_BACKDATE_DAYS} from '@/pages/wells/dailyReportTypes.ts';
import {authStatusAtom, cachedSessionAtom, isOnlineAtom} from './authAtoms';
import {useCognitoReachable} from './useCognitoReachable';
import {isFieldRole} from '@/types/auth';
import {pushNotificationAtom} from './notificationAtoms';
import {runTicketsAtom} from './runTicketAtoms';
import {
   flaggedTankDropsAtom,
   flaggedTankDropsLoadingAtom,
   flaggedTankDropsErrorAtom,
   type FlaggedTankDrop,
} from './tankDropAtoms';

// Same 7-day window as the run-ticket/daily-report reconciliation above
// — Duane's own confirmed answer, 2026-09-30 ("Same 7-day window as
// other reconciliation (Recommended)").
const RECONCILIATION_WINDOW_DAYS = MAX_BACKDATE_DAYS;

function recentDateStrings(days: number): string[] {
   return Array.from({length: days}, (_, i) => dayjs().subtract(i, 'day').format('YYYY-MM-DD'));
}

/**
 * Chunk 7 (2026-09-30) — the unexplained tank-drop flag, the final chunk
 * of the tank-gauge/pulled-bottoms feature set. Full spec confirmed with
 * the client (chunk 1's original design doc) and re-confirmed via this
 * chunk's own clarifying questions with Duane:
 *   - Flag any tank whose gauge drops >= TANK_DROP_THRESHOLD_BBLS
 *     day-over-day (see lib/tankDropReconciliation.ts).
 *   - "Explained" = a same-tank, same-day Run Ticket or Pulled Bottoms
 *     record exists (late entry OK; the date must match exactly).
 *   - RunTicket-to-tank matching: trimmed/case-insensitive against
 *     Tank.tankNumber, with a single-tank-lease fallback (any same-day
 *     ticket for the lease counts). A same-day ticket that names some
 *     OTHER tank (2+ tanks on the lease) gets noted right on the flag
 *     rather than silently dropped or raised as its own ticket flag.
 *   - A tank shared by several wells shows every one of those wells its
 *     own allocated share of the drop, on its own WellsListPage row.
 *   - Admin/office only — this is an office-side review tool. A field
 *     User never populates or is notified about any of this (same
 *     isFieldRole/isAdminOrSuperAdmin split as every other admin-only
 *     feature in this app — see types/auth.ts).
 *
 * Mounted globally in App.tsx alongside useRunTicketReconciliation(),
 * same "runs no matter what page you're on" reasoning — see that hook's
 * own doc comment.
 *
 * NOTIFICATION TRIGGER — Duane's spec says the snackbar is "shown on
 * Dashboard/WellsListPage load, NOT real-time push," unlike the three
 * poll-driven toasts useRunTicketReconciliation.ts fires from anywhere
 * in the app. Read literally: fire once when arriving at (or already
 * on, on first app load) Dashboard or Wells List, as soon as this hook's
 * own data has finished loading — not continuously every
 * RECONCILIATION_POLL_MS-style poll while sitting on either page. See
 * notifiedForThisVisitRef below for how that "once per visit, resets on
 * navigating away and back" behavior is implemented. This is this
 * session's own reading of that spec line, not a separate question
 * Duane was asked directly — worth a quick confirm with him that this
 * matches what he had in mind.
 */
export function useTankDropReconciliation() {
   const cachedSession = useAtomValue(cachedSessionAtom);
   const fieldUser = cachedSession != null && isFieldRole(cachedSession.role);
   const authStatus = useAtomValue(authStatusAtom);
   const isAuthed = authStatus === 'authenticated' || authStatus === 'offline-cached';
   const isOnline = useAtomValue(isOnlineAtom);
   const cognitoReachable = useCognitoReachable(isOnline);
   // Admin/office feature only (Duane's spec) — a field User is never
   // even queried against, let alone notified.
   const canQuery = isAuthed && isOnline && cognitoReachable && !fieldUser;

   const setDrops = useSetAtom(flaggedTankDropsAtom);
   const setLoading = useSetAtom(flaggedTankDropsLoadingAtom);
   const setErrorAtom = useSetAtom(flaggedTankDropsErrorAtom);
   const pushNotification = useSetAtom(pushNotificationAtom);
   const navigate = useNavigate();
   const location = useLocation();

   // Already populated, unrestricted, and windowed to this same 7 days
   // for Admin/SuperAdmin by useRunTicketReconciliation.ts — reused
   // directly rather than firing a second RunTicket query (Duane's own
   // answer, 2026-09-30).
   const tickets = useAtomValue(runTicketsAtom);

   const entryDates = useMemo(() => recentDateStrings(RECONCILIATION_WINDOW_DAYS), []);
   // One extra day before the window so the OLDEST in-window date still
   // has a prior reading to diff against — priorGaugeReading needs a
   // reading strictly before it, which can fall just outside entryDates.
   const gaugeDateStart = useMemo(
      () =>
         dayjs(entryDates[entryDates.length - 1])
            .subtract(1, 'day')
            .format('YYYY-MM-DD'),
      [entryDates],
   );
   const gaugeDateEnd = entryDates[0];

   const {
      data: wellsData,
      loading: wellsLoading,
      error: wellsError,
   } = useQuery(Well_AllDocument, {
      errorPolicy: 'all',
      skip: !canQuery,
   });
   const {
      data: tanksData,
      loading: tanksLoading,
      error: tanksError,
   } = useQuery(Tank_AllWhereDocument, {
      variables: {includeDeleted: false},
      errorPolicy: 'all',
      skip: !canQuery,
   });
   const {
      data: readingsData,
      loading: readingsLoading,
      error: readingsError,
   } = useQuery(TankGaugeReading_AllWhereDocument, {
      // No tankId scoping — unlike useLeaseTankGauge.ts (one lease at a
      // time), this hook reasons about every tank company-wide at once.
      variables: {gaugeDateStart, gaugeDateEnd, includeDeleted: false},
      errorPolicy: 'all',
      skip: !canQuery,
   });
   const {
      data: pulledData,
      loading: pulledLoading,
      error: pulledError,
   } = useQuery(PulledBottom_AllWhereDocument, {
      // No pulledBottomSearch — Admin/SuperAdmin, unrestricted, same
      // convention as every other query here. No date-range args exist
      // on this query (see that file's own header comment) — low/rare
      // data volume makes fetching everything and filtering client-side
      // by exact pulledDate acceptable.
      errorPolicy: 'all',
      skip: !canQuery,
   });

   const wells = useMemo(
      () => (wellsData?.well_All ?? []).map((row) => toWell(row)).filter((w): w is Well => w !== null && w.active),
      [wellsData],
   );
   const leaseIdByWellId = useMemo(() => new Map(wells.map((w) => [w.id, w.leaseId])), [wells]);

   const activeTanks = useMemo(
      (): TankDropTank[] =>
         (tanksData?.tank_AllWhere ?? [])
            .filter((t): t is NonNullable<typeof t> => t != null && !t.isDeleted)
            .map((t) => ({
               id: String(t.id),
               tankNumber: t.tankNumber ?? '',
               leaseId: t.leaseId != null ? String(t.leaseId) : '',
               multiplier: t.multiplier ?? 0,
            })),
      [tanksData],
   );
   const activeTankCountByLeaseId = useMemo(() => {
      const map = new Map<string, number>();
      for (const t of activeTanks) map.set(t.leaseId, (map.get(t.leaseId) ?? 0) + 1);
      return map;
   }, [activeTanks]);

   const readingsByTankId = useMemo(() => {
      const all = toTankGaugeReadings(readingsData?.tankGaugeReading_AllWhere);
      const map = new Map<string, (typeof all)[number][]>();
      for (const r of all) {
         const list = map.get(r.tankId) ?? [];
         list.push(r);
         map.set(r.tankId, list);
      }
      return map;
   }, [readingsData]);

   const pulledBottoms = useMemo(
      () => toPulledBottoms(pulledData?.pulledBottom_AllWhere, new Map(), new Map()),
      [pulledData],
   );

   // report_TankAllocations only supports a single CURRENT `asOfDate`
   // snapshot, not a historical range (see that query's own header
   // comment) — there is no way to ask "what was this well's share of
   // this tank on <a past drop date>". Documented simplification (same
   // "flag the assumption in code rather than silently make it"
   // convention as tankGaugeHelperText's hasValidAllocation warning):
   // every well share below uses TODAY's allocation snapshot for every
   // drop in the window, whatever date it actually happened on. Given
   // gauge tracking itself only started 2026-09-30 (the same day as this
   // chunk), the risk of a real allocation CHANGE happening mid-window is
   // negligible for now — worth revisiting if this feature is still
   // using report_TankAllocations this way once historical allocation
   // changes become common.
   const activeWellIds = useMemo(() => wells.map((w) => w.id), [wells]);
   const today = useMemo(() => dayjs().format('YYYY-MM-DD'), []);
   const needsAllocation = canQuery && activeWellIds.length > 0;
   const {
      data: allocationsData,
      loading: allocationsLoading,
      error: allocationsError,
   } = useQuery(Report_TankAllocationsDocument, {
      variables: {asOfDate: today, wellIds: activeWellIds, pumperIds: undefined, ...leaseAllocationReportArgs(today)},
      skip: !needsAllocation,
      errorPolicy: 'all',
   });

   // report_TankAllocations rows carry tankNumber + wellId, not a real
   // tankId — resolved back to a specific Tank via (that well's leaseId,
   // tankNumber), NEVER RunTicket.leaseId-style shortcuts (this join uses
   // the real Well.leaseId, already trustworthy). Necessary company-wide
   // (unlike useLeaseTankGauge.ts, which never needs this join — it only
   // ever looks at one lease's own tanks, where a bare tankNumber can't
   // collide) since the same tankNumber ("1", "2", ...) can appear on
   // more than one lease.
   const allocationRowsByTankId = useMemo(() => {
      const rows = (allocationsData?.report_TankAllocations ?? []).filter((r): r is NonNullable<typeof r> => r != null);
      const tankByLeaseAndNumber = new Map<string, TankDropTank>();
      for (const t of activeTanks) {
         tankByLeaseAndNumber.set(`${t.leaseId}:${t.tankNumber.trim().toLowerCase()}`, t);
      }
      const map = new Map<
         string,
         {wellId: string; wellName: string; allocation: number; hasValidAllocation: boolean}[]
      >();
      // Lease-level rows (allocationType "lease", no tankNumber) apply to
      // every active tank on the well's lease — fan them out first.
      const tankNumbersByLeaseId = new Map<string, string[]>();
      for (const t of activeTanks) {
         const list = tankNumbersByLeaseId.get(t.leaseId) ?? [];
         list.push(t.tankNumber);
         tankNumbersByLeaseId.set(t.leaseId, list);
      }
      const expandedRows = dedupeAllocationRowsByWellAndTank(
         expandLeaseRowsToTanks(rows, (wellId) => {
            const leaseId = leaseIdByWellId.get(wellId);
            return (leaseId && tankNumbersByLeaseId.get(leaseId)) || [];
         }),
      );
      for (const row of expandedRows) {
         const wellId = String(row.wellId);
         const leaseId = leaseIdByWellId.get(wellId);
         if (!leaseId) continue;
         const tank = tankByLeaseAndNumber.get(`${leaseId}:${row.tankNumber.trim().toLowerCase()}`);
         if (!tank) continue; // defensive: row names a tank not in our active-tanks set, same convention as useLeaseTankGauge.ts
         const list = map.get(tank.id) ?? [];
         list.push({
            wellId,
            wellName: row.wellName ?? '',
            allocation: row.allocation ?? 0,
            hasValidAllocation: row.hasValidAllocation,
         });
         map.set(tank.id, list);
      }
      return map;
   }, [allocationsData, activeTanks, leaseIdByWellId]);

   // Per-tank well/percent shares — same three-tier trivial/single-tank/
   // multi-tank logic useLeaseTankGauge.ts already established for the
   // Daily Report drawer, generalized here to run across every lease at
   // once rather than one lease per hook call.
   const wellAllocationsByTankId = useMemo(() => {
      const map = new Map<
         string,
         {wellId: string; wellName: string; allocationPercent: number; hasValidAllocation: boolean}[]
      >();
      for (const tank of activeTanks) {
         const leaseWells = wells.filter((w) => w.leaseId === tank.leaseId);
         const tankCountOnLease = activeTankCountByLeaseId.get(tank.leaseId) ?? 0;
         const rows = allocationRowsByTankId.get(tank.id) ?? [];
         if (tankCountOnLease <= 1) {
            // Single-tank lease: every active well on it is assumed to
            // share this one tank.
            const wellCountOnTank = Math.max(leaseWells.length, 1);
            map.set(
               tank.id,
               leaseWells.map((w) => {
                  const row = rows.find((r) => r.wellId === w.id);
                  return {
                     wellId: w.id,
                     wellName: w.wellName,
                     allocationPercent: row?.hasValidAllocation ? row.allocation : 100 / wellCountOnTank,
                     hasValidAllocation: row?.hasValidAllocation === true,
                  };
               }),
            );
            continue;
         }
         // 2+ tanks on this lease: only wells with a real
         // report_TankAllocations row against THIS tank share it — a
         // well with no row for any of the lease's tanks isn't assumed
         // to be on this one (same "fall back to nothing rather than
         // guess" call as useLeaseTankGauge.ts).
         const wellCountOnTank = rows.length || 1;
         map.set(
            tank.id,
            rows.map((row) => ({
               wellId: row.wellId,
               wellName: row.wellName,
               allocationPercent: row.hasValidAllocation ? row.allocation : 100 / wellCountOnTank,
               hasValidAllocation: row.hasValidAllocation,
            })),
         );
      }
      return map;
   }, [activeTanks, wells, activeTankCountByLeaseId, allocationRowsByTankId]);

   const dropsLoading =
      wellsLoading || tanksLoading || readingsLoading || pulledLoading || (needsAllocation && allocationsLoading);

   const flaggedDrops = useMemo((): FlaggedTankDrop[] => {
      if (dropsLoading) return [];
      // Backend doesn't support tankGaugeReading_AllWhere / pulledBottom_AllWhere
      // yet (2026-09-30). Without gauge readings there's nothing to flag; without
      // pulled-bottoms there's no way to tell which drops are explained, so
      // flagging would falsely report everything. Show no flags rather than
      // wrong ones; the rest of the app is unaffected.
      if (
         isBackendUnsupportedQuery(readingsError, readingsData?.tankGaugeReading_AllWhere != null) ||
         isBackendUnsupportedQuery(pulledError, pulledData?.pulledBottom_AllWhere != null)
      ) {
         return [];
      }

      const pulledDatesByTankId = new Map<string, Set<string>>();
      for (const pb of pulledBottoms) {
         const set = pulledDatesByTankId.get(pb.tankId) ?? new Set<string>();
         set.add(pb.pulledDate);
         pulledDatesByTankId.set(pb.tankId, set);
      }

      // Same local-calendar-date bucketing useRunTicketReconciliation.ts's
      // own 2026-09-25 fix established (see localCalendarDate's own doc
      // comment) — and grouped by the ticket's RESOLVED leaseId (via
      // wellId -> Well.leaseId), never RunTicket.leaseId itself.
      const ticketsByLeaseAndDate = new Map<string, RunTicketTankCandidate[]>();
      for (const t of tickets) {
         const leaseId = leaseIdByWellId.get(t.wellId);
         if (!leaseId) continue;
         const key = `${leaseId}:${localCalendarDate(t.pickupDate)}`;
         const list = ticketsByLeaseAndDate.get(key) ?? [];
         list.push({id: t.id, ticketNumber: t.ticketNumber, tank: t.tank});
         ticketsByLeaseAndDate.set(key, list);
      }

      const result: FlaggedTankDrop[] = [];
      for (const tank of activeTanks) {
         const readings = readingsByTankId.get(tank.id) ?? [];
         if (readings.length === 0) continue;
         const events = computeTankDropEvents(tank, readings, entryDates);
         if (events.length === 0) continue;

         const tankCountOnLease = activeTankCountByLeaseId.get(tank.leaseId) ?? 0;
         const wellShares = wellAllocationsByTankId.get(tank.id) ?? [];
         const pulledDates = pulledDatesByTankId.get(tank.id);

         for (const event of events) {
            const sameDayLeaseTickets = ticketsByLeaseAndDate.get(`${tank.leaseId}:${event.dropDate}`) ?? [];
            const sameDayTankPulledBottoms = pulledDates?.has(event.dropDate)
               ? [{id: `${tank.id}:${event.dropDate}`}]
               : [];
            const {explained, mismatchNote} = explainTankDrop({
               tank: {tankNumber: tank.tankNumber},
               activeTankCountOnLease: tankCountOnLease,
               sameDayLeaseTickets,
               sameDayTankPulledBottoms,
            });
            result.push({
               tankId: tank.id,
               tankNumber: tank.tankNumber,
               leaseId: tank.leaseId,
               dropDate: event.dropDate,
               priorDate: event.priorDate,
               dropBbls: event.dropBbls,
               explained,
               mismatchNote,
               wells: wellShares.map((share) => ({
                  wellId: share.wellId,
                  wellName: share.wellName,
                  allocationPercent: share.allocationPercent,
                  hasValidAllocation: share.hasValidAllocation,
                  allocatedShareBbls: computeAllocatedOilProduction(event.dropBbls, share.allocationPercent),
               })),
            });
         }
      }
      return result;
   }, [
      dropsLoading,
      readingsError,
      readingsData,
      pulledError,
      pulledData,
      pulledBottoms,
      tickets,
      leaseIdByWellId,
      activeTanks,
      readingsByTankId,
      activeTankCountByLeaseId,
      wellAllocationsByTankId,
      entryDates,
   ]);

   useEffect(() => {
      setLoading(dropsLoading);
   }, [dropsLoading, setLoading]);

   useEffect(() => {
      setErrorAtom(wellsError ?? tanksError ?? readingsError ?? pulledError ?? allocationsError ?? null);
   }, [wellsError, tanksError, readingsError, pulledError, allocationsError, setErrorAtom]);

   useEffect(() => {
      setDrops(flaggedDrops);
   }, [flaggedDrops, setDrops]);

   // See this hook's own doc comment on the notification trigger — once
   // per "visit" to Dashboard/WellsListPage, not a continuous poll-driven
   // push. notifiedForThisVisitRef resets whenever the route changes AWAY
   // from those two pages, so navigating off and back (or a fresh app
   // load landing on either) can notify again; it does NOT reset just
   // because new data streamed in while already sitting on the page —
   // that would be the "real-time push" behavior Duane's spec explicitly
   // ruled out.
   const isDashboardOrWellsList =
      location.pathname === routerLinks.dashboard || location.pathname === routerLinks.wells;
   const notifiedForThisVisitRef = useRef(false);
   const lastPathRef = useRef(location.pathname);
   useEffect(() => {
      if (lastPathRef.current !== location.pathname) {
         lastPathRef.current = location.pathname;
         notifiedForThisVisitRef.current = false;
      }
   }, [location.pathname]);

   useEffect(() => {
      if (!isDashboardOrWellsList || dropsLoading || notifiedForThisVisitRef.current) return;
      const unexplainedCount = flaggedDrops.filter((d) => !d.explained).length;
      if (unexplainedCount === 0) return;
      notifiedForThisVisitRef.current = true;
      pushNotification({
         message:
            unexplainedCount === 1
               ? '1 tank has an unexplained gauge drop.'
               : `${unexplainedCount} tanks have an unexplained gauge drop.`,
         // See notificationAtoms.ts's own doc comment on `kind` — recomputed
         // live by NotificationSnackbar from unexplainedTankDropCountAtom
         // rather than trusting this frozen string.
         kind: 'unexplainedTankDrops',
         actionLabel: 'Review',
         severity: 'warning',
         onAction: () => navigate(routerLinks.wells),
      });
   }, [isDashboardOrWellsList, dropsLoading, flaggedDrops, pushNotification, navigate]);
}
